import React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { Group } from '@lightspeed/flame/Group';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text, TextContent } from '@lightspeed/flame/Text';

import { createReactRenderer } from 'helpers/react-renderer';
import { clearInvoicingStateAfterCustomerRemoval } from 'root/apps/app/src/payments/SaleManager';

const RemoveCustomerButton: React.FC = () => {
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = React.useState(false);

  const runRemoveCustomerFlow = async () => {
    try {
      await clearInvoicingStateAfterCustomerRemoval();
    } catch {
      window.alertUser('Something went wrong. Refresh your page.');
    }
    window.merchantos.utils.openUrl('register.php?form_name=clear_customer');
  };

  const doesOrderHaveDeposit = (): boolean => {
    const paymentLinkTile = document.querySelector('[data-test="payment-link-deposit-tile"]');
    const accountDepositTile = document.querySelector('[data-test="account-deposit-row"]');

    const isElementHidden = (element: Element) => {
      if (!element) {
        return false;
      }
      const style = window.getComputedStyle(element);
      return style.display === 'none';
    };

    // Check if accountDepositTile exists and is not hidden, or if paymentLinkTile has children
    return !!(
      (accountDepositTile && !isElementHidden(accountDepositTile)) ||
      (paymentLinkTile && paymentLinkTile.childElementCount > 0)
    );
  };

  return (
    <>
      <button
        type="button"
        id="customerRemoveButton"
        data-test="customer-remove-button"
        onClick={() => {
          if (doesOrderHaveDeposit()) {
            setConfirmationModalIsOpen(true);
          } else {
            runRemoveCustomerFlow().catch(() => {});
          }
        }}
        className="gui-def-button"
      >
        <i className="icon-trash" />
        &nbsp;Remove
      </button>

      <Modal isOpen={confirmationModalIsOpen}>
        <ModalHeader
          data-test="confirmation-remove-customer"
          onCloseClicked={() => setConfirmationModalIsOpen(false)}
        >
          Remove customer?
        </ModalHeader>
        <ModalBody style={{ maxWidth: '24rem' }}>
          <TextContent>
            <Text>
              Removing the customer will also remove the pending deposit in the current sale.
            </Text>
          </TextContent>
        </ModalBody>
        <ModalFooter>
          <Flex justifyContent="right">
            <Group>
              <Button
                data-test="confirmation-remove-customer-back"
                onClick={() => setConfirmationModalIsOpen(false)}
              >
                Back
              </Button>
              <Button
                fill={true}
                variant="danger"
                onClick={() => {
                  runRemoveCustomerFlow()
                    .catch(() => {})
                    .finally(() => setConfirmationModalIsOpen(false));
                }}
              >
                Remove customer
              </Button>
            </Group>
          </Flex>
        </ModalFooter>
      </Modal>
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: () => <RemoveCustomerButton />,
  selector: '[data-react-root="RemoveCustomerButton"]',
});

history.onChangeState(
  { pathname: '/register.php', form_name: undefined, id: undefined, view: undefined },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default RemoveCustomerButton;
